import {
  CheckMarkOutlineIcon,
  CloseOutlineIcon,
  ErrorOutlineIcon,
  InfoOutlineIcon,
  WarningOutlineIcon,
} from '@applyboard/ui-icons';
import React from 'react';
import { InternalButton, InternalButtonProps } from '../InternalButton';
import { InternalButtonLink, InternalButtonLinkProps } from '../InternalButtonLink';
import { Intent, useFullTheme } from '~/design';
import { down, up } from '~/design/breakpoint';
import {
  ChildComparisonArgumentType,
  ChildType,
  Subset,
  isChildOneOfType,
  isChildType,
  useResponsive,
  hasSymbol,
} from '~/utils';
import { IconWrapper } from '../IconWrapper';

interface InternalNoticeContextProps {
  disabled?: boolean;
  intent?: InternalNoticeProps['intent'];
}

const InternalNoticeContext = React.createContext<InternalNoticeContextProps | null>(null);

export const symbolMap = {
  Button: Symbol('Notice.Button'),
  ButtonLink: Symbol('Notice.ButtonLink'),
  CloseButton: Symbol('Notice.CloseButton'),
} as const;

export interface InternalNoticeProps {
  children: React.ReactNode;

  /** The elevation of the notice. */
  elevation?: 'z2';

  /** Determines the color and icon of the notice */
  intent?: Subset<Intent, 'primary' | 'secondary' | 'positive' | 'negative' | 'warning'>;

  /**
   * Will show an "X" button on the right if provided
   * @deprecated Use `InternalNotice.CloseButton` instead.
   */
  onClose?: () => void;

  /**
   * The text to show in the action button
   * @deprecated Use `InternalNotice.Button` instead.
   * */
  actionText?: string;

  /**
   * Called when user clicks the action button
   * @deprecated Use `InternalNotice.Button` instead.
   * */
  onAction?: () => void;

  /** The width of the Notice */
  width?: 'md' | 'fill';

  /** If true, will disable all buttons in the notice */
  disabled?: boolean;
}

export const NOTICE_MD_WIDTH = 487;

/**
 * A notice displays a brief, important message in a way that attracts the user's attention without interrupting the user's task.
 */
export function InternalNotice({
  actionText,
  children,
  disabled,
  elevation,
  intent = 'primary',
  onAction,
  onClose,
  width = 'fill',
}: InternalNoticeProps) {
  if (!['primary', 'secondary', 'positive', 'negative', 'warning'].includes(intent)) {
    throw new Error(`'${intent}' intent is not allowed`);
  }

  if (onAction && !actionText) {
    throw new Error('actionText must be provided if onAction is provided');
  }

  if (actionText && !onAction) {
    throw new Error('onAction must be provided if actionText is provided');
  }

  const { colors, elevations, spacing, typography, borderRadiuses } = useFullTheme();
  const responsive = useResponsive();

  const childrenArray = React.Children.toArray(children);
  const buttonChild = getChild(childrenArray, InternalNotice.Button, symbolMap.Button);
  const buttonLinkChild = getChild(childrenArray, InternalNotice.ButtonLink, symbolMap.ButtonLink);
  const closeButtonChild = getChild(
    childrenArray,
    InternalNotice.CloseButton,
    symbolMap.CloseButton,
  );
  const otherChildren = childrenArray.filter(
    child =>
      !isChildOneOfType(child, [
        InternalNotice.CloseButton,
        InternalNotice.Button,
        InternalNotice.ButtonLink,
      ]) &&
      !hasSymbol(child, symbolMap.Button) &&
      !hasSymbol(child, symbolMap.ButtonLink) &&
      !hasSymbol(child, symbolMap.CloseButton),
  );
  if (!!closeButtonChild && !!onClose) {
    throw new Error('Notice.CloseButton and onClose cannot be used together.');
  }
  if (!!actionText || !!onAction) {
    if (buttonChild) {
      throw new Error('Notice.Button and onAction cannot be used together.');
    }
    if (buttonLinkChild) {
      throw new Error('Notice.ButtonLink and onAction cannot be used together.');
    }
  }
  if (!!buttonChild && !!buttonLinkChild) {
    throw new Error('Notice.Button and Notice.ButtonLink cannot be used together.');
  }

  const buttonsContent = (
    <>
      {actionText && onAction && (
        <InternalNotice.Button onClick={() => onAction()}>{actionText}</InternalNotice.Button>
      )}
      {onClose && <InternalNotice.CloseButton onClick={onClose} />}

      {buttonChild}
      {buttonLinkChild}
    </>
  );

  const IntentIcon = {
    negative: ErrorOutlineIcon,
    positive: CheckMarkOutlineIcon,
    primary: InfoOutlineIcon,
    secondary: InfoOutlineIcon,
    warning: WarningOutlineIcon,
  }[intent];

  return (
    <InternalNoticeContext.Provider value={{ disabled, intent }}>
      <div
        css={[
          responsive.css(typography.bodyM),
          {
            alignItems: 'flex-start',
            backgroundColor: {
              magic: colors.bgMagic,
              negative: colors.bgNegative,
              positive: colors.bgPositive,
              primary: colors.bgPrimaryHighlightHover,
              secondary: colors.bgSecondaryHighlightHover,
              warning: colors.bgWarning,
            }[intent],
            borderRadius: borderRadiuses.container,
            boxShadow: elevation ? elevations[elevation] : undefined,
            color: {
              magic: colors.textMagicStark,
              negative: colors.textNegativeStark,
              positive: colors.textPositiveStark,
              primary: colors.textPrimaryStark,
              secondary: colors.textSecondaryStark,
              warning: colors.textWarningStark,
            }[intent],
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[2],
            maxWidth: '100%',
            minHeight: spacing[16],
            paddingBlock: spacing[5],
            paddingInline: spacing[4],
            width: width === 'fill' ? '100%' : NOTICE_MD_WIDTH,
          },
        ]}
      >
        <div css={{ display: 'flex', width: '100%' }}>
          <div css={{ flexShrink: 0, lineHeight: 0 }}>
            <IconWrapper allowInteractions={false} intent={intent} selector="div">
              <IntentIcon />
            </IconWrapper>
          </div>
          <div
            css={{
              marginLeft: spacing[4],
              flexGrow: 1,
              // minWidth is needed to ensure the content does not exceed the width available
              // See: https://defensivecss.dev/tip/flexbox-min-content-size/
              minWidth: 0,
            }}
          >
            {otherChildren}
          </div>
          <div
            css={{
              flexShrink: 0,
              marginBlock: `-${spacing[3]}`,
              [up('sm')]: {
                display: 'flex',
                gap: spacing[2],
                marginLeft: spacing[2],
                marginRight: `-${spacing[2]}`,
              },
            }}
          >
            <div css={{ [down('sm')]: { display: 'none' } }}>{buttonsContent}</div>
            {closeButtonChild}
          </div>
        </div>

        <div
          css={{
            display: 'block',
            paddingLeft: spacing[10],
            paddingRight: closeButtonChild ? spacing[10] : 0,
            width: '100%',
            [up('sm')]: {
              display: 'none',
            },
          }}
        >
          {buttonsContent}
        </div>
      </div>
    </InternalNoticeContext.Provider>
  );
}

export interface InternalNoticeCloseButtonProps {
  /** A callback function that is invoked when the button is clicked. */
  onClick: () => void;
}

InternalNotice.CloseButton = function InternalNoticeCloseButton({
  onClick,
}: InternalNoticeCloseButtonProps) {
  const context = React.useContext(InternalNoticeContext);
  if (!context) {
    throw new Error('`Notice.CloseButton` can only be rendered inside `Notice`');
  }
  return (
    <InternalButton
      aria-label="Close notice"
      disabled={context.disabled}
      emphasis="transparent"
      intent={context.intent}
      leadIcon={CloseOutlineIcon}
      onClick={() => onClick?.()}
    />
  );
};

export interface InternalNoticeButtonProps
  extends Pick<
    InternalButtonProps,
    | 'children'
    | 'disabled'
    | 'id'
    | 'leadIcon'
    | 'loading'
    | 'onBlur'
    | 'onClick'
    | 'onFocus'
    | 'onPointerDown'
    | 'onPointerLeave'
    | 'onPointerMove'
    | 'trailIcon'
  > {
  /** A callback function that is invoked when the button is clicked. */
  onClick: () => void;
}

InternalNotice.Button = function InternalNoticeButton({
  children,
  disabled: _disabled,
  id,
  leadIcon,
  loading,
  onBlur,
  onClick,
  onFocus,
  onPointerDown,
  onPointerLeave,
  onPointerMove,
  trailIcon,
}: InternalNoticeButtonProps) {
  const context = React.useContext(InternalNoticeContext);
  if (!context) {
    throw new Error('`Notice.Button` can only be rendered inside `Notice`');
  }

  const { disabled, intent } = context;

  return (
    <InternalButton
      disabled={disabled || _disabled}
      emphasis="highlighted"
      id={id}
      intent={intent}
      leadIcon={leadIcon}
      loading={loading}
      onBlur={onBlur}
      onClick={() => onClick?.()}
      onFocus={onFocus}
      onPointerDown={onPointerDown}
      onPointerLeave={onPointerLeave}
      onPointerMove={onPointerMove}
      trailIcon={trailIcon}
      width={{ xs: 'fill', sm: 'hug' }}
    >
      {children}
    </InternalButton>
  );
};

export type InternalNoticeButtonLinkProps = Pick<
  InternalButtonLinkProps,
  | 'children'
  | 'disabled'
  | 'href'
  | 'id'
  | 'leadIcon'
  | 'loading'
  | 'mode'
  | 'onBlur'
  | 'onClick'
  | 'onFocus'
  | 'onPointerDown'
  | 'onPointerLeave'
  | 'onPointerMove'
  | 'trailIcon'
>;

InternalNotice.ButtonLink = function InternalNoticeButtonLink({
  children,
  disabled: _disabled,
  href,
  id,
  leadIcon,
  loading,
  mode,
  onBlur,
  onClick,
  onFocus,
  onPointerDown,
  onPointerLeave,
  onPointerMove,
  trailIcon,
}: InternalNoticeButtonLinkProps) {
  const context = React.useContext(InternalNoticeContext);
  if (!context) {
    throw new Error('`Notice.ButtonLink` can only be rendered inside `Notice`');
  }

  return (
    <InternalButtonLink
      disabled={context.disabled || _disabled}
      emphasis="highlighted"
      href={href}
      id={id}
      intent={context.intent}
      leadIcon={leadIcon}
      loading={loading}
      mode={mode}
      onBlur={onBlur}
      onClick={() => onClick?.()}
      onFocus={onFocus}
      onPointerDown={onPointerDown}
      onPointerLeave={onPointerLeave}
      onPointerMove={onPointerMove}
      trailIcon={trailIcon}
      width={{ xs: 'fill', sm: 'hug' }}
    >
      {children}
    </InternalButtonLink>
  );
};

function getChild(children: ChildType[], type: ChildComparisonArgumentType, symbol: symbol) {
  return children.find(child => isChildType(child, type) || hasSymbol(child, symbol));
}
